import { Product, StockState } from '../types';

/**
 * Single source of truth for interpreting a product's stock level, so the
 * products page, the cart and the admin panel can never disagree about whether
 * something is low or sold out.
 *
 * Products with track_inventory off are 'untracked': unlimited, and no stock
 * messaging is shown at all. That is the default, so nothing changes for a
 * product until an admin opts it in from the Quantities tab.
 */
export const getStockState = (product: Product): StockState => {
  if (!product.track_inventory) return 'untracked';

  const count = product.inventory_count ?? 0;
  if (count <= 0) return 'out';

  const threshold = product.low_stock_threshold ?? 0;
  if (count <= threshold) return 'low';

  return 'in-stock';
};

/**
 * Units a customer may still order, or null when the product is unlimited.
 */
export const getAvailableStock = (product: Product): number | null => {
  if (!product.track_inventory) return null;
  return Math.max(0, product.inventory_count ?? 0);
};

/**
 * How many more units can be added, accounting for what is already in the cart.
 * Returns null when the product is unlimited.
 */
export const getRemainingToAdd = (product: Product, quantityInCart: number): number | null => {
  const available = getAvailableStock(product);
  if (available === null) return null;
  return Math.max(0, available - quantityInCart);
};

/**
 * Whether the remaining allowance is low enough to be worth telling the
 * customer about. Compares what is *still available after their cart* against
 * the threshold, so a big cart against healthy stock still triggers the
 * warning once it eats into the last of it.
 */
export const isRemainingLow = (product: Product, remaining: number | null): boolean => {
  if (remaining === null) return false;
  return remaining <= (product.low_stock_threshold ?? 0);
};

export const getStockLabel = (product: Product): string | null => {
  switch (getStockState(product)) {
    case 'out':
      return 'OUT OF STOCK';
    case 'low': {
      const count = product.inventory_count ?? 0;
      return `Only ${count} left`;
    }
    default:
      return null;
  }
};
